import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Tabs,
  Container,
  Title,
  Text,
  Paper,
  Group,
  Button,
  Stack,
  TextInput,
  Textarea,
  Select,
  ColorInput,
  Alert,
  SimpleGrid,
  Box,
  ThemeIcon,
  Avatar,
  Card,
  Badge,
  Divider,
  NumberInput,
  Skeleton,
  Switch,
  ActionIcon,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { FileUploader } from "../../../../components/FileUploader";
import AccessTierControl from "../../../../components/AccessTierControl";
import {
  IconArrowLeft,
  IconDeviceFloppy,
  IconPalette,
  IconDetails,
  IconPhoneCheck,
  IconLock,
  IconGift,
  IconMessageQuestion,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { Endpoints } from "../../../../lib/endpoints";
import {
  PerformRequest,
  UploadFiles,
} from "../../../../hooks/usePerformRequest";
import { HttpCodes } from "../../../../lib/data";
import { Campaign } from "../index";

type CQ = {
  key: string;
  prompt: string;
  type: "choice" | "rating" | "text";
  options: string[];
  required: boolean;
};

/**
 * In-app questions for a campaign (a group of opportunities). Members see these
 * once they've completed `threshold` of the campaign's opportunities; each is
 * answered once per campaign. Saves independently via UpdateCampaign (partial body).
 */
function CampaignQuestionsEditor({
  campaignId,
  initialQuestions,
  initialThreshold,
}: {
  campaignId: string;
  initialQuestions: CQ[];
  initialThreshold: number;
}) {
  const [qs, setQs] = useState<CQ[]>(
    (initialQuestions || []).map((q) => ({
      key: q.key || "",
      prompt: q.prompt || "",
      type: q.type || "choice",
      options: q.options?.length ? q.options : ["", ""],
      required: q.required !== false,
    })),
  );
  const [threshold, setThreshold] = useState<number>(initialThreshold ?? 2);
  const [saving, setSaving] = useState(false);

  const patchQ = (i: number, p: Partial<CQ>) =>
    setQs((c) => c.map((q, j) => (j === i ? { ...q, ...p } : q)));

  const save = async () => {
    for (const q of qs) {
      if (!q.key.trim() || !q.prompt.trim())
        return notifications.show({
          title: "Missing",
          message: "Every question needs a key and a prompt.",
          color: "red",
        });
      if (q.type === "choice") {
        const good = q.options.map((o) => o.trim()).filter(Boolean);
        if (good.length < 2)
          return notifications.show({
            title: "Options",
            message: `"${q.key || "?"}" needs at least 2 options.`,
            color: "red",
          });
      }
    }
    const keys = qs.map((q) => q.key.trim());
    if (new Set(keys).size !== keys.length)
      return notifications.show({
        title: "Duplicate",
        message: "Question keys must be unique.",
        color: "red",
      });
    const clean = qs.map((q) => ({
      key: q.key.trim(),
      prompt: q.prompt.trim(),
      type: q.type,
      required: q.required,
      options:
        q.type === "choice"
          ? q.options.map((o) => o.trim()).filter(Boolean)
          : [],
    }));
    setSaving(true);
    const ep = Endpoints.UpdateCampaign(campaignId);
    const r = await PerformRequest({
      method: ep.method,
      route: ep.route,
      data: { questions: clean, questionsAfterCompletions: threshold },
    });
    setSaving(false);
    if (r.status === HttpCodes.Success)
      notifications.show({
        title: "Saved",
        message: "Campaign questions updated — live in the app.",
        color: "green",
      });
    else
      notifications.show({
        title: "Error",
        message: "Could not save questions.",
        color: "red",
      });
  };

  return (
    <Stack>
      <Alert color="green" variant="light" icon={<IconMessageQuestion size={18} />}>
        These questions pop up in the app once a member has completed the number
        of this campaign's opportunities set below. Each is answered once per
        campaign — never shown again after answering. No reward is attached.
      </Alert>
      <Paper withBorder p="md" radius="md">
        <NumberInput
          label="Show questions after this many completed opportunities"
          description="e.g. 2 = a member sees the questions after finishing 2 of this campaign's opportunities."
          min={0}
          max={50}
          value={threshold}
          onChange={(v) => setThreshold(Number(v) || 0)}
          w={420}
        />
      </Paper>

      {qs.map((q, i) => (
        <Paper key={i} withBorder p="md" radius="md">
          <Group justify="space-between" mb="sm">
            <Text fw={600}>Question {i + 1}</Text>
            <ActionIcon
              color="red"
              variant="light"
              onClick={() => setQs((c) => c.filter((_, j) => j !== i))}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TextInput
              label="Key (unique, no spaces)"
              placeholder="e.g. enjoyed_content"
              value={q.key}
              onChange={(e) =>
                patchQ(i, {
                  key: e.currentTarget.value.replace(/\s+/g, "_").toLowerCase(),
                })
              }
            />
            <Select
              label="Type"
              data={[
                { value: "choice", label: "Multiple choice" },
                { value: "rating", label: "Star rating (1–5)" },
                { value: "text", label: "Free text" },
              ]}
              value={q.type}
              onChange={(v) => patchQ(i, { type: (v as CQ["type"]) || "choice" })}
            />
          </SimpleGrid>
          <TextInput
            mt="sm"
            label="Question"
            placeholder="e.g. Did you enjoy Michael's videos?"
            value={q.prompt}
            onChange={(e) => patchQ(i, { prompt: e.currentTarget.value })}
          />
          {q.type === "choice" && (
            <Stack gap="xs" mt="sm">
              <Text size="sm" fw={500}>
                Options
              </Text>
              {q.options.map((o, oi) => (
                <Group key={oi} gap="xs" wrap="nowrap">
                  <TextInput
                    style={{ flex: 1 }}
                    placeholder={`Option ${oi + 1}`}
                    value={o}
                    onChange={(e) =>
                      patchQ(i, {
                        options: q.options.map((x, k) =>
                          k === oi ? e.currentTarget.value : x,
                        ),
                      })
                    }
                  />
                  <ActionIcon
                    color="red"
                    variant="subtle"
                    disabled={q.options.length <= 1}
                    onClick={() =>
                      patchQ(i, {
                        options: q.options.filter((_, k) => k !== oi),
                      })
                    }
                  >
                    <IconTrash size={14} />
                  </ActionIcon>
                </Group>
              ))}
              <Button
                size="xs"
                variant="light"
                leftSection={<IconPlus size={14} />}
                onClick={() => patchQ(i, { options: [...q.options, ""] })}
                w="fit-content"
              >
                Add option
              </Button>
            </Stack>
          )}
          <Switch
            mt="sm"
            label="Required (must answer to continue)"
            checked={q.required}
            onChange={(e) => patchQ(i, { required: e.currentTarget.checked })}
          />
        </Paper>
      ))}

      <Group>
        <Button
          variant="light"
          leftSection={<IconPlus size={16} />}
          onClick={() =>
            setQs((c) => [
              ...c,
              {
                key: "",
                prompt: "",
                type: "choice",
                options: ["", ""],
                required: true,
              },
            ])
          }
        >
          Add question
        </Button>
        <Button
          leftSection={<IconDeviceFloppy size={16} />}
          loading={saving}
          onClick={save}
        >
          Save questions
        </Button>
      </Group>
    </Stack>
  );
}

const CampaignUpdate = () => {
  const navigate = useNavigate();
  const { campaignId } = useParams();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadedCampaign, setLoadedCampaign] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<string | null>("details");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [levels, setLevels] = useState<any[]>([]);

  const form = useForm({
    initialValues: {
      title: "",
      clientName: "",
      description: "",
      status: "draft",
      primaryColor: "#19752e",
      secondaryColor: "#E8FBF6",
      logoUrl: "",
      bannerUrl: "",
      endDate: null as Date | null,
      minLevel: 1,
      eligibility: {
        type: "open",
        minMissionCompletions: 0,
      },
      completionBonus: {
        amount: 0,
        currency: "COIN",
      },
      isLocked: false,
      lockReason: "",
      squadsOnly: false,
    },
    validate: {
      title: (v) => (!v.trim() ? "Title is required" : null),
      clientName: (v) => (!v.trim() ? "Client name is required" : null),
      endDate: (v) =>
        v && v < new Date(Date.now() + 86400000)
          ? "Expiry date must be at least 1 day in the future"
          : null,
    },
  });

  useEffect(() => {
    if (form.values.title) {
      document.title = `Update Campaign: ${form.values.title} | Virel Admin`;
    } else {
      document.title = "Update Campaign | Virel Admin";
    }
  }, [form.values.title]);

  useEffect(() => {
    const fetchCampaign = async () => {
      if (!campaignId) return;
      setIsLoading(true);
      const endpoint = Endpoints.GetCampaign(campaignId);
      const r = await PerformRequest<Campaign>({
        method: endpoint.method,
        route: endpoint.route,
      });

      if (r.status === HttpCodes.Success && r.data) {
        const c = r.data;
        setLoadedCampaign(c);
        form.setValues({
          title: c.title || "",
          clientName: c.clientName || "",
          description: c.description || "",
          status: c.status || "draft",
          primaryColor: c.branding?.primaryColor || "#19752e",
          secondaryColor: c.branding?.secondaryColor || "#E8FBF6",
          logoUrl: c.branding?.logoUrl || "",
          bannerUrl: c.branding?.bannerUrl || "",
          endDate: c.endDate ? new Date(c.endDate) : null,
          minLevel: c.minLevel || 1,
          eligibility: {
            type: c.eligibility?.type || "open",
            minMissionCompletions: c.eligibility?.minMissionCompletions || 0,
          },
          completionBonus: {
            amount: c.completionBonus?.amount || 0,
            currency: c.completionBonus?.currency || "COIN",
          },
          isLocked: c.isLocked || false,
          lockReason: c.lockReason || "",
          squadsOnly: c.squadsOnly || false,
        });
      } else {
        setError("Failed to load campaign");
      }
      setIsLoading(false);
    };

    fetchCampaign();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  useEffect(() => {
    const fetchLevels = async () => {
      const endpoint = Endpoints.GetLevels();
      const r = await PerformRequest<any>({
        method: endpoint.method,
        route: endpoint.route,
      });
      if (r.status === HttpCodes.Success) {
        setLevels(r.data.data || []);
      }
    };
    fetchLevels();
  }, []);

  const submit = async () => {
    if (!campaignId) return;
    setIsSubmitting(true);
    setError(null);

    // No need to re-validate endDate if it's unchanged and valid?
    // Actually validation runs on submit. If date is old (was valid when created but now in past), allow it?
    // Probably user updates it to future if they touch it.
    // For now, standard validation applies. If user edits an expired campaign, they might need to update date.

    const validation = form.validate();
    if (validation.hasErrors) {
      setIsSubmitting(false);
      notifications.show({
        title: "Validation Error",
        message: "Please fix the form errors before proceeding.",
        color: "red",
      });
      // Scroll to first error field
      const firstErrorKey = Object.keys(validation.errors)[0];
      if (firstErrorKey) {
        const errorElement = document.querySelector(
          `[name="${firstErrorKey}"], #${firstErrorKey}, [id*="${firstErrorKey}"]`,
        );
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
          (errorElement as HTMLElement).focus?.();
        }
      }
      return;
    }

    let finalLogoUrl = form.values.logoUrl;
    let finalBannerUrl = form.values.bannerUrl;

    try {
      if (logoFile) {
        const res = await UploadFiles([logoFile]);
        if (
          (res[0].status === HttpCodes.Success ||
            res[0].status === HttpCodes.Created) &&
          res[0].data?.data?.url
        ) {
          finalLogoUrl = res[0].data.data.url;
        } else {
          throw new Error("Logo upload failed");
        }
      }
      if (bannerFile) {
        const res = await UploadFiles([bannerFile]);
        if (
          (res[0].status === HttpCodes.Success ||
            res[0].status === HttpCodes.Created) &&
          res[0].data?.data?.url
        ) {
          finalBannerUrl = res[0].data.data.url;
        } else {
          throw new Error("Banner upload failed");
        }
      }
    } catch (e) {
      console.error(e);
      setIsSubmitting(false);
      setError("Failed to upload images. Please try again.");
      return;
    }

    // Double check we aren't sending blobs
    if (
      finalLogoUrl?.startsWith("blob:") ||
      finalBannerUrl?.startsWith("blob:")
    ) {
      setIsSubmitting(false);
      setError("Image upload incomplete. Please try again.");
      return;
    }

    const endpoint = Endpoints.UpdateCampaign(campaignId);
    const payload = {
      title: form.values.title,
      clientName: form.values.clientName,
      description: form.values.description,
      status: form.values.status,
      endDate: form.values.endDate,
      minLevel: form.values.minLevel,
      branding: {
        primaryColor: form.values.primaryColor,
        secondaryColor: form.values.secondaryColor,
        logoUrl: finalLogoUrl,
        bannerUrl: finalBannerUrl,
      },
      eligibility: form.values.eligibility,
      completionBonus: form.values.completionBonus,
      isLocked: form.values.isLocked,
      lockReason: form.values.isLocked ? form.values.lockReason : undefined,
      squadsOnly: form.values.squadsOnly,
    };

    const r = await PerformRequest({
      method: endpoint.method,
      route: endpoint.route,
      data: payload,
      onError: () => setError("Failed to update campaign"),
    });

    if (r.status === HttpCodes.Success) {
      navigate("/d/campaigns");
    } else {
      setError(r.message || "Failed to update campaign");
    }
    setIsSubmitting(false);
  };

  if (isLoading) {
    return (
      <Container size="xl" pt={40}>
        <Stack gap="xl">
          <Skeleton height={50} width="50%" />
          <SimpleGrid cols={{ base: 1, lg: 2 }} spacing={40}>
            <Skeleton height={400} />
            <Skeleton height={600} />
          </SimpleGrid>
        </Stack>
      </Container>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #0F0518 0%, #1A0B2E 100%)",
        paddingBottom: 40,
      }}
    >
      <div
        className="header-bg"
        style={{
          height: 200,
          background: "linear-gradient(135deg, #145223 0%, #19752e 100%)",
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          borderBottomRightRadius: 40,
          borderBottomLeftRadius: 40,
        }}
      ></div>

      <Container
        size="xl"
        style={{ position: "relative", zIndex: 1, paddingTop: 40 }}
      >
        <Stack gap="xl">
          {/* Header */}
          <Group justify="space-between" align="center">
            <div>
              <Button
                variant="white"
                color="dark"
                size="xs"
                radius="xl"
                leftSection={<IconArrowLeft size={16} />}
                onClick={() => navigate(-1)}
                mb={12}
                style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
              >
                Back to Campaigns
              </Button>
              <Title order={1} c="white">
                Edit Campaign
              </Title>
              <Text c="rgba(255,255,255,0.7)" size="sm">
                Update campaign details and preview changes instantly.
              </Text>
            </div>
            <Button
              size="md"
              radius="xl"
              variant="gradient"
              gradient={{ from: "brand", to: "green", deg: 45 }}
              leftSection={<IconDeviceFloppy size={20} />}
              loading={isSubmitting}
              onClick={submit}
              style={{ boxShadow: "0 4px 12px rgba(108, 77, 255, 0.4)" }}
            >
              Save Changes
            </Button>
          </Group>

          <Tabs value={activeTab} onChange={setActiveTab} mt="lg">
            <Tabs.List mb="md">
              <Tabs.Tab value="details">Details</Tabs.Tab>
              <Tabs.Tab
                value="questions"
                leftSection={<IconMessageQuestion size={16} />}
              >
                Questions
              </Tabs.Tab>
              <Tabs.Tab value="analytics">Analytics</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="details">
              {error && (
                <Alert color="red" variant="filled" radius="md">
                  {error}
                </Alert>
              )}

              <SimpleGrid cols={{ base: 1, lg: 2 }} spacing={40}>
                {/* Left Column: Forms */}
                <Stack gap="xl">
                  <Paper
                    shadow="sm"
                    p="xl"
                    radius="lg"
                    style={{
                      background: "rgba(255, 255, 255, 0.95)",
                      backdropFilter: "blur(20px)",
                      border: "1px solid rgba(255,255,255,0.1)",
                    }}
                  >
                    <Group mb="lg">
                      <ThemeIcon
                        size="lg"
                        radius="md"
                        variant="light"
                        color="green"
                      >
                        <IconDetails size={20} />
                      </ThemeIcon>
                      <Title order={4}>Campaign Details</Title>
                    </Group>

                    <Stack gap="md">
                      <TextInput
                        size="md"
                        label="Campaign Title"
                        placeholder="e.g. Opay Winter Promo"
                        withAsterisk
                        {...form.getInputProps("title")}
                      />
                      <TextInput
                        size="md"
                        label="Client Name"
                        placeholder="e.g. Opay"
                        withAsterisk
                        {...form.getInputProps("clientName")}
                      />
                      <Textarea
                        size="md"
                        label="Description"
                        placeholder="Briefly describe the campaign goals..."
                        minRows={3}
                        {...form.getInputProps("description")}
                      />
                      <Select
                        size="md"
                        label="Status"
                        data={[
                          { value: "draft", label: "Draft" },
                          { value: "active", label: "Active" },
                          { value: "completed", label: "Completed" },
                        ]}
                        {...form.getInputProps("status")}
                      />
                      <DatePickerInput
                        size="md"
                        label="Expiry Date (Optional)"
                        placeholder="Pick a date"
                        minDate={new Date(Date.now() + 86400000)}
                        clearable
                        {...form.getInputProps("endDate")}
                      />
                    </Stack>
                  </Paper>

                  <Paper
                    shadow="sm"
                    p="xl"
                    radius="lg"
                    style={{
                      background: "rgba(255, 255, 255, 0.95)",
                      backdropFilter: "blur(20px)",
                      border: "1px solid rgba(255,255,255,0.1)",
                    }}
                  >
                    <Group mb="lg">
                      <ThemeIcon
                        size="lg"
                        radius="md"
                        variant="light"
                        color="pink"
                      >
                        <IconPalette size={20} />
                      </ThemeIcon>
                      <Title order={4}>Branding & Assets</Title>
                    </Group>

                    <Stack gap="md">
                      <SimpleGrid cols={2}>
                        <ColorInput
                          size="md"
                          label="Primary Color"
                          disallowInput={false}
                          {...form.getInputProps("primaryColor")}
                        />
                        <ColorInput
                          size="md"
                          label="Secondary Color"
                          disallowInput={false}
                          {...form.getInputProps("secondaryColor")}
                        />
                      </SimpleGrid>
                      <FileUploader
                        label="Logo"
                        value={form.values.logoUrl}
                        onChange={(url) => {
                          form.setFieldValue("logoUrl", url);
                          if (!url) setLogoFile(null);
                        }}
                        onFileSelect={(file) => {
                          setLogoFile(file);
                          form.setFieldValue(
                            "logoUrl",
                            URL.createObjectURL(file),
                          );
                        }}
                        placeholder="Upload Logo"
                      />
                      <FileUploader
                        label="Banner"
                        value={form.values.bannerUrl}
                        onChange={(url) => {
                          form.setFieldValue("bannerUrl", url);
                          if (!url) setBannerFile(null);
                        }}
                        onFileSelect={(file) => {
                          setBannerFile(file);
                          form.setFieldValue(
                            "bannerUrl",
                            URL.createObjectURL(file),
                          );
                        }}
                        placeholder="Upload Banner"
                      />
                    </Stack>
                  </Paper>

                  <Paper
                    shadow="sm"
                    p="xl"
                    radius="lg"
                    style={{
                      background: "rgba(255, 255, 255, 0.95)",
                      backdropFilter: "blur(20px)",
                      border: "1px solid rgba(255,255,255,0.1)",
                    }}
                  >
                    <Group mb="lg">
                      <ThemeIcon
                        size="lg"
                        radius="md"
                        variant="light"
                        color="orange"
                      >
                        <IconLock size={20} />
                      </ThemeIcon>
                      <Title order={4}>Eligibility Criteria</Title>
                    </Group>

                    <Stack gap="md">
                      <Select
                        size="md"
                        label="Access Type"
                        data={[
                          { value: "open", label: "Open for All" },
                          { value: "criteria", label: "Criteria Based" },
                        ]}
                        {...form.getInputProps("eligibility.type")}
                      />

                      {form.values.eligibility.type === "criteria" && (
                        <NumberInput
                          size="md"
                          label="Minimum Activity Completions"
                          description="Users must have completed this many activities to join."
                          min={0}
                          {...form.getInputProps(
                            "eligibility.minMissionCompletions",
                          )}
                        />
                      )}

                      <Select
                        size="md"
                        label="Minimum User Level"
                        description="Users must reach this level to access this campaign."
                        data={levels
                          .sort((a, b) => a.level - b.level)
                          .map((l) => ({
                            value: String(l.level),
                            label: `Level ${l.level} - ${l.name} (${l.minCoins}+ coins)`,
                          }))}
                        value={String(form.values.minLevel)}
                        onChange={(v) =>
                          form.setFieldValue("minLevel", Number(v) || 1)
                        }
                      />

                      <Divider my="sm" />

                      <Switch
                        label="Squads Only Campaign"
                        description="If enabled, only users who are members of a Squad can participate in this campaign."
                        size="md"
                        {...form.getInputProps("squadsOnly", { type: "checkbox" })}
                      />

                      <Divider my="sm" />

                      <Switch
                        label="Lock Campaign"
                        description="Prevent users from participating while displaying a lock reason"
                        size="md"
                        {...form.getInputProps("isLocked", { type: "checkbox" })}
                      />

                      {form.values.isLocked && (
                        <TextInput
                          size="md"
                          label="Lock Reason"
                          description="Short message explaining why it's locked (e.g., 'Requires VIP status')"
                          withAsterisk
                          {...form.getInputProps("lockReason")}
                        />
                      )}
                    </Stack>
                  </Paper>

                  <Paper
                    shadow="sm"
                    p="xl"
                    radius="lg"
                    style={{
                      background: "rgba(255, 255, 255, 0.95)",
                      backdropFilter: "blur(20px)",
                      border: "1px solid rgba(255,255,255,0.1)",
                    }}
                  >
                    <Group mb="lg">
                      <ThemeIcon
                        size="lg"
                        radius="md"
                        variant="light"
                        color="teal"
                      >
                        <IconGift size={20} />
                      </ThemeIcon>
                      <Title order={4}>Rewards & Gamification</Title>
                    </Group>

                    <Stack gap="md">
                      <SimpleGrid cols={2}>
                        <NumberInput
                          size="md"
                          label="Completion Bonus"
                          description="Reward for completing all activities"
                          min={0}
                          rightSection={
                            <Text size="xs" mr="xs">
                              Coins
                            </Text>
                          }
                          {...form.getInputProps("completionBonus.amount")}
                        />
                        <TextInput
                          size="md"
                          label="Currency"
                          description="Currency code (e.g. COIN)"
                          {...form.getInputProps("completionBonus.currency")}
                        />
                      </SimpleGrid>
                    </Stack>
                  </Paper>
                </Stack>

                {/* Right Column: Preview */}
                <Stack
                  gap="md"
                  align="center"
                  style={{ position: "sticky", top: 20 }}
                >
                  <Group>
                    <ThemeIcon radius="xl" color="gray" variant="light">
                      <IconPhoneCheck size={18} />
                    </ThemeIcon>
                    <Text fw={600} c="dimmed" size="sm" tt="uppercase">
                      Live Mobile Preview
                    </Text>
                  </Group>

                  {/* Phone Frame */}
                  <Box
                    style={{
                      width: 320,
                      height: 640,
                      background: "#fff",
                      borderRadius: 40,
                      border: "8px solid #333",
                      boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
                      overflow: "hidden",
                      position: "relative",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {/* Status Bar Mock */}
                    <Group justify="space-between" px="lg" pt="md" pb="xs">
                      <Text size="xs" fw={700}>
                        9:41
                      </Text>
                      <Group gap={4}>
                        <div
                          style={{
                            width: 14,
                            height: 10,
                            background: "#000",
                            borderRadius: 2,
                          }}
                        />
                      </Group>
                    </Group>

                    {/* App Content */}
                    <div
                      style={{
                        flex: 1,
                        overflowY: "auto",
                        position: "relative",
                      }}
                    >
                      {/* Detailed Campaign View Mock */}
                      <div
                        style={{
                          height: 180,
                          background: form.values.bannerUrl
                            ? `url(${form.values.bannerUrl}) center/cover`
                            : `${form.values.primaryColor}20`,
                          position: "relative",
                          display: "flex",
                          alignItems: "flex-end",
                          padding: 16,
                        }}
                      >
                        {!form.values.bannerUrl && (
                          <Text
                            size="xs"
                            c="dimmed"
                            ta="center"
                            style={{
                              width: "100%",
                              position: "absolute",
                              top: "40%",
                            }}
                          >
                            Banner Image
                          </Text>
                        )}
                      </div>

                      <div
                        style={{
                          padding: 20,
                          marginTop: -20,
                          position: "relative",
                        }}
                      >
                        <Card
                          radius="lg"
                          shadow="sm"
                          p="lg"
                          style={{ background: "white" }}
                        >
                          <Group align="flex-start" wrap="nowrap">
                            <Avatar
                              src={form.values.logoUrl}
                              size="lg"
                              radius="md"
                              style={{
                                background: form.values.primaryColor,
                                color: "white",
                              }}
                            >
                              {form.values.clientName?.[0] || "C"}
                            </Avatar>
                            <div>
                              <Text fw={700} size="lg" lh={1.2}>
                                {form.values.title || "Campaign Title"}
                              </Text>
                              <Text size="sm" c="dimmed">
                                {form.values.clientName || "Client Name"}
                              </Text>
                            </div>
                          </Group>

                          <Divider my="md" />

                          <Text
                            size="xs"
                            fw={700}
                            c="dimmed"
                            tt="uppercase"
                            mb={4}
                          >
                            About
                          </Text>
                          <Text size="sm" c="gray.7" lineClamp={3}>
                            {form.values.description ||
                              "This is where the campaign description will appear in the app. Users will read this to understand the campaign."}
                          </Text>

                          <Group mt="lg">
                            <Badge
                              color={
                                form.values.status === "active"
                                  ? "green"
                                  : "gray"
                              }
                              variant="light"
                            >
                              {form.values.status}
                            </Badge>
                            <Badge color="brand" variant="light">
                              5 Activities
                            </Badge>
                          </Group>
                        </Card>

                        <Stack mt="md">
                          <Card shadow="xs" radius="md" p="sm" withBorder>
                            <Group justify="space-between">
                              <Group gap="xs">
                                <Box
                                  w={32}
                                  h={32}
                                  bg={form.values.secondaryColor}
                                  style={{ borderRadius: 8 }}
                                />
                                <div>
                                  <Text size="sm" fw={600}>
                                    Example Activity
                                  </Text>
                                  <Text size="xs" c="dimmed">
                                    Spotify • 50 Coins
                                  </Text>
                                </div>
                              </Group>
                              <Button
                                size="xs"
                                radius="xl"
                                variant="light"
                                color="gray"
                              >
                                Start
                              </Button>
                            </Group>
                          </Card>
                        </Stack>
                      </div>
                    </div>

                    {/* Bottom Bar Mock */}
                    <div
                      style={{
                        padding: 16,
                        borderTop: "1px solid #eee",
                        background: "white",
                      }}
                    >
                      <Button
                        fullWidth
                        radius="xl"
                        style={{
                          background: form.values.primaryColor,
                          boxShadow: `0 4px 12px ${form.values.primaryColor}66`,
                        }}
                      >
                        Join Campaign
                      </Button>
                    </div>
                  </Box>
                </Stack>
              </SimpleGrid>

              {campaignId && (
                <AccessTierControl
                  kind="campaign"
                  id={campaignId}
                  currentTier={loadedCampaign?.accessTier}
                  currentAllowUsers={loadedCampaign?.allowUsers || []}
                />
              )}
            </Tabs.Panel>

            <Tabs.Panel value="questions" pt="md">
              <CampaignQuestionsEditor
                key={loadedCampaign?._id || campaignId}
                campaignId={campaignId!}
                initialQuestions={loadedCampaign?.questions || []}
                initialThreshold={loadedCampaign?.questionsAfterCompletions ?? 2}
              />
            </Tabs.Panel>

            <Tabs.Panel value="analytics">
              <CampaignAnalytics campaignId={campaignId} />
            </Tabs.Panel>
          </Tabs>
        </Stack>
      </Container>
    </div>
  );
};

const CampaignAnalytics = ({ campaignId }: { campaignId?: string }) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!campaignId) return;
    const fetch = async () => {
      setLoading(true);
      const ep = Endpoints.GetCampaignAnalytics(campaignId);
      const r = await PerformRequest<any>({
        method: ep.method,
        route: ep.route,
      });
      if (r.status === HttpCodes.Success) {
        setData(r.data);
      }
      setLoading(false);
    };
    fetch();
  }, [campaignId]);

  if (loading)
    return (
      <Stack align="center" mt="xl">
        <Skeleton height={8} radius="xl" />
        <Text size="sm" c="dimmed">
          Loading analytics...
        </Text>
      </Stack>
    );
  if (!data) return <Text mt="xl">No data available yet.</Text>;

  return (
    <Stack gap="lg" mt="md">
      <SimpleGrid cols={{ base: 1, md: 3 }}>
        <Paper p="lg" withBorder radius="md">
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
            Total Participants
          </Text>
          <Text size="xl" fw={700} fz={32}>
            {data.totalParticipants}
          </Text>
        </Paper>
        <Paper p="lg" withBorder radius="md">
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
            Total XP Distributed
          </Text>
          <Text size="xl" fw={700} fz={32}>
            {(data.totalXp || 0).toLocaleString()}
          </Text>
        </Paper>
        <Paper p="lg" withBorder radius="md">
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
            Completion Rate
          </Text>
          <Group align="flex-end" gap="xs">
            <Text size="xl" fw={700} fz={32}>
              {data.campaignCompletions || 0}
            </Text>
            <Text
              size="sm"
              c={data.totalParticipants > 0 ? "teal" : "dimmed"}
              mb={6}
              fw={600}
            >
              (
              {data.totalParticipants > 0
                ? Math.round(
                    ((data.campaignCompletions || 0) / data.totalParticipants) *
                      100,
                  )
                : 0}
              %)
            </Text>
          </Group>
        </Paper>
      </SimpleGrid>

      <Paper p="lg" withBorder radius="md">
        <Title order={4} mb="md">
          Daily Activity
        </Title>
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            height: 200,
            gap: 4,
          }}
        >
          {data.dailyActivity?.length ? (
            data.dailyActivity.map((day: any) => (
              <div
                key={day._id}
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    width: "80%",
                    height: `${Math.min(day.count * 10, 100)}%`,
                    backgroundColor: "#19752e",
                    borderRadius: "4px 4px 0 0",
                    minHeight: 4,
                  }}
                />
              </div>
            ))
          ) : (
            <div
              style={{ width: "100%", textAlign: "center", paddingBottom: 20 }}
            >
              <Text c="dimmed" size="sm">
                No activity recorded yet.
              </Text>
            </div>
          )}
        </div>
      </Paper>
    </Stack>
  );
};

export default CampaignUpdate;
